import React, { useState, useEffect, useCallback } from 'react';
import { Building, Plus, Edit2, Power, PowerOff, Loader2, Search, X } from 'lucide-react';
import api from 'services/api';
import bankCredentialService from '../services/bankCredentialService';
import ModalBankCredential from './bankCredentials/ModalBankCredential';
import NotificationToast from 'components/common/NotificationToast';
import { usePermissions } from 'hooks/usePermissions';

const BankCredentials = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('config.bank_credentials.view');
  const canManage = hasPermission('config.bank_credentials.manage');

  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [banks, setBanks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [notification, setNotification] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    company_id: '',
    bank_id: '',
    is_active: '',
  });
  const [searchTerm, setSearchTerm] = useState('');

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  const loadBanks = useCallback(async () => {
    try {
      const response = await bankCredentialService.getAvailableBanks();
      setBanks(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar bancos:', error);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await bankCredentialService.list(filters);
      setCredentials(response.data || []);
    } catch (error) {
      showNotification('Erro ao carregar credenciais', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, showNotification]);

  useEffect(() => {
    if (canView) {
      loadBanks();
      loadCompanies();
    }
  }, [canView, loadBanks, loadCompanies]);

  useEffect(() => {
    if (canView) {
      loadData();
    }
  }, [canView, loadData]);

  const handleCreate = () => {
    setEditingCredential(null);
    setModalOpen(true);
  };

  const handleEdit = (credential) => {
    setEditingCredential(credential);
    setModalOpen(true);
  };

  const handleToggleActive = async (credential) => {
    try {
      await bankCredentialService.toggleActive(credential.id);
      showNotification('Status alterado com sucesso');
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Erro ao alterar status', 'error');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCredential(null);
  };

  const handleModalSuccess = () => {
    showNotification(editingCredential ? 'Credencial atualizada com sucesso' : 'Credencial criada com sucesso');
    loadData();
    handleModalClose();
  };

  const filteredCredentials = credentials.filter((cred) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const companyName = cred.company?.name?.toLowerCase() || '';
      const bankName = banks.find(b => b.id === cred.bank_id)?.name?.toLowerCase() || '';
      return companyName.includes(search) || bankName.includes(search);
    }
    return true;
  });

  if (!canView) {
    return (
      <div>
        <div className="rounded-lg p-4 border theme-surface" style={{ borderColor: 'var(--border)' }}>
          <p className="theme-text">Você não tem permissão para visualizar credenciais bancárias.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NotificationToast notification={notification} />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold theme-text">Credenciais Bancárias</h1>
          <p className="mt-1 theme-text-muted">Gerencie credenciais e certificados para integração bancária</p>
        </div>
        {canManage && (
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5" />
            Nova Credencial
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="rounded-lg border p-4 theme-surface" style={{ borderColor: 'var(--border)' }}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold theme-text mb-1">Empresa</label>
            <select
              value={filters.company_id}
              onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 theme-surface"
              style={{ borderColor: 'var(--border)' }}
            >
              <option value="">Todas</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold theme-text mb-1">Banco</label>
            <select
              value={filters.bank_id}
              onChange={(e) => setFilters({ ...filters, bank_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 theme-surface"
              style={{ borderColor: 'var(--border)' }}
            >
              <option value="">Todos</option>
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold theme-text mb-1">Status</label>
            <select
              value={filters.is_active}
              onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 theme-surface"
              style={{ borderColor: 'var(--border)' }}
            >
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold theme-text mb-1">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-muted w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Empresa ou banco..."
                className="w-full border rounded-lg px-10 py-2 theme-surface"
                style={{ borderColor: 'var(--border)' }}
              />
            </div>
          </div>
        </div>

        {(filters.company_id || filters.bank_id || filters.is_active) && (
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setFilters({ company_id: '', bank_id: '', is_active: '' })}
              className="flex items-center gap-2 text-sm theme-text-muted hover:opacity-80"
            >
              <X className="w-4 h-4" />
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Tabela */}
      <div className="rounded-lg border overflow-hidden theme-surface" style={{ borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto theme-text-muted" />
            <p className="mt-4 theme-text-muted">Carregando credenciais...</p>
          </div>
        ) : filteredCredentials.length === 0 ? (
          <div className="p-12 text-center">
            <Building className="w-12 h-12 mx-auto theme-text-muted" />
            <p className="mt-4 theme-text">Nenhuma credencial encontrada</p>
            {canManage && (
              <button
                onClick={handleCreate}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Criar Primeira Credencial
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y" style={{ borderColor: 'var(--border)' }}>
            <thead className="theme-surface-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Banco
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider theme-text">
                  Criado em
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider theme-text">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
              {filteredCredentials.map((credential) => (
                <tr key={credential.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building className="w-5 h-5 theme-text-muted mr-2" />
                      <span className="text-sm font-medium theme-text">
                        {credential.company?.name || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm theme-text">
                      {banks.find(b => b.id === credential.bank_id)?.name || credential.bank_id}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        credential.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'theme-surface-muted theme-text'
                      }`}
                    >
                      {credential.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm theme-text-muted">
                    {new Date(credential.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {canManage && (
                        <>
                          <button
                            onClick={() => handleEdit(credential)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Editar"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(credential)}
                            className={credential.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                            title={credential.is_active ? 'Desativar' : 'Ativar'}
                          >
                            {credential.is_active ? <PowerOff className="w-5 h-5" /> : <Power className="w-5 h-5" />}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <ModalBankCredential
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          credential={editingCredential}
          banks={banks}
        />
      )}
    </div>
  );
};

export default BankCredentials;
